import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import { TrendingUp, Phone, Users, CheckCircle, Calendar, Target, Loader2, Gift, Trophy, Zap, IndianRupee, UserPlus } from 'lucide-react';
import PhoneNumberAlert from '../components/PhoneNumberAlert';

const SalesPerformance = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pointsData, setPointsData] = useState(null);
  const [competitions, setCompetitions] = useState([]);
  const user = authService.getCurrentUser();

  useEffect(() => {
    fetchMyStats();
    fetchMyPoints();
    fetchActiveCompetitions();
  }, []);

  const fetchMyStats = async () => {
    try {
      const response = await api.getMyPerformance('sales');
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch performance data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyPoints = async () => {
    try {
      const response = await api.getMyPoints();
      setPointsData(response.data);
    } catch (error) {
      console.error('Error fetching points:', error);
    }
  };

  const fetchActiveCompetitions = async () => {
    try {
      const response = await api.getCompetitions('active');
      console.log('Competitions response:', response.data);
      setCompetitions(response.data || []);
    } catch (error) {
      console.error('Error fetching competitions:', error);
      setCompetitions([]);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const overallConversion = stats?.conversion_rate?.toFixed(1) || 0;
  const todayDate = new Date().toLocaleDateString('en-IN', { 
    weekday: 'long', 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  });

  return (
    <div data-testid="sales-performance" className="max-w-[1200px] mx-auto space-y-6">
      {/* Phone Number Alert - Shows if user hasn't updated phone */}
      <PhoneNumberAlert />
      
      <div>
        <h1 className="text-3xl font-bold font-headings text-slate-900 mb-2">
          My Performance
        </h1>
        <p className="text-slate-600 font-body">Hello {user?.name}, here's your sales performance.</p>
      </div>

      {/* TODAY'S PERFORMANCE - Highlighted Section */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 rounded-xl p-6 text-white shadow-lg" data-testid="today-performance-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Zap className="w-6 h-6 text-yellow-300" />
            <h2 className="text-xl font-bold">Today's Performance</h2>
          </div>
          <span className="text-sm bg-white/20 px-3 py-1 rounded-full">{todayDate}</span>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm" data-testid="today-new-leads">
            <div className="flex items-center gap-2 mb-2">
              <UserPlus className="w-5 h-5 text-blue-200" />
              <span className="text-sm text-white/80">New Leads</span>
            </div>
            <p className="text-3xl font-bold">{stats?.today_new_leads || 0}</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm" data-testid="today-sales">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-300" />
              <span className="text-sm text-white/80">Sales</span>
            </div>
            <p className="text-3xl font-bold">{stats?.today_sales || 0}</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm" data-testid="today-revenue">
            <div className="flex items-center gap-2 mb-2">
              <IndianRupee className="w-5 h-5 text-yellow-300" />
              <span className="text-sm text-white/80">Revenue</span>
            </div>
            <p className="text-3xl font-bold">₹{(stats?.today_revenue || 0).toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Points & Conversion Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Points Earned */}
        <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-6 text-white" data-testid="points-earned-card">
          <div className="flex items-center gap-2 mb-3">
            <Gift size={20} />
            <span className="text-sm font-medium uppercase">Points Earned</span>
          </div>
          <p className="text-4xl font-bold">{pointsData?.total_points || 0}</p>
          <p className="text-amber-100 text-sm mt-2">
            = ₹{pointsData?.rupee_value || 0} (This Month)
          </p>
        </div>

        {/* Overall Conversion */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={20} />
            <span className="text-sm font-medium uppercase">Conversion Rate</span>
          </div>
          <p className="text-4xl font-bold">{overallConversion}%</p>
          <p className="text-purple-100 text-sm mt-2">
            {stats?.customers || 0} converted / {stats?.total_leads || 0} leads
          </p>
        </div>

        {/* Monthly Revenue */}
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-6 text-white">
          <div className="flex items-center gap-2 mb-3">
            <Target size={20} />
            <span className="text-sm font-medium uppercase">This Month</span>
          </div>
          <p className="text-4xl font-bold">₹{(stats?.monthly_revenue || 0).toLocaleString()}</p>
          <p className="text-emerald-100 text-sm mt-2">
            {stats?.monthly_customers || 0} sales this month
          </p>
        </div>

        {/* Total Revenue */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={20} />
            <span className="text-sm font-medium uppercase">Total Revenue</span>
          </div>
          <p className="text-4xl font-bold">₹{(stats?.total_revenue || 0).toLocaleString()}</p>
          <p className="text-blue-100 text-sm mt-2">
            {stats?.customers || 0} total sales
          </p>
        </div>
      </div>

      {/* Active Competitions Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-5 h-5 text-amber-500" />
          <h2 className="text-lg font-bold text-slate-900">Active Competitions</h2>
        </div>
        {competitions.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {competitions.map(comp => (
              <div key={comp.id} className="border rounded-lg p-4 bg-slate-50" data-testid={`competition-${comp.id}`}>
                <h3 className="font-semibold text-slate-900">{comp.name}</h3>
                
                {/* Staged Competition Display */}
                {comp.winner_type === 'staged' && comp.stages?.length > 0 ? (
                  <div className="mt-3">
                    <p className="text-xs text-slate-500 mb-2 font-medium">🎯 Milestone Rewards:</p>
                    <div className="space-y-2">
                      {comp.stages.map((stage, idx) => {
                        const stageTarget = stage.sales_target || stage.target_value || 0;
                        const currentValue = comp.my_progress?.current_value || 0;
                        const isAchieved = currentValue >= stageTarget;
                        const progressPercent = Math.min(100, (currentValue / stageTarget) * 100);
                        
                        return (
                          <div key={idx} className={`p-2 rounded ${isAchieved ? 'bg-green-100 border border-green-300' : 'bg-white border border-slate-200'}`}>
                            <div className="flex justify-between items-center text-sm">
                              <span className={`font-medium ${isAchieved ? 'text-green-700' : 'text-slate-700'}`}>
                                {isAchieved ? '✓' : `#${idx + 1}`} {stageTarget} {comp.competition_type === 'revenue' ? '₹' : 'Sales'}
                              </span>
                              <span className={`font-bold ${isAchieved ? 'text-green-600' : 'text-amber-600'}`}>
                                {stage.reward_points} pts
                              </span>
                            </div>
                            {!isAchieved && (
                              <div className="mt-1">
                                <div className="w-full bg-slate-200 rounded-full h-1.5">
                                  <div 
                                    className="h-1.5 rounded-full bg-indigo-500"
                                    style={{ width: `${progressPercent}%` }}
                                  />
                                </div>
                                <p className="text-xs text-slate-500 mt-0.5">
                                  {currentValue} / {stageTarget} ({Math.round(progressPercent)}%)
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-2 p-2 bg-amber-50 rounded text-center">
                      <p className="text-xs text-amber-700">
                        <strong>Your Progress:</strong> {comp.my_progress?.current_value || 0} {comp.competition_type === 'revenue' ? '₹' : 'Sales'}
                        {comp.my_progress?.stages_achieved?.length > 0 && (
                          <span className="ml-2 text-green-600">
                            ✓ Earned: {comp.my_progress.total_stage_points || 0} pts
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                ) : (
                  /* Non-staged competition display */
                  <>
                    <p className="text-sm text-slate-600 mt-1">
                      Target: {comp.competition_type === 'dual' ? (
                        <>
                          {comp.target_value && `₹${comp.target_value.toLocaleString()}`}
                          {comp.target_value && comp.sales_target && ' OR '}
                          {comp.sales_target && `${comp.sales_target} Sales`}
                        </>
                      ) : comp.competition_type === 'revenue' ? (
                        `₹${(comp.target_value || 0).toLocaleString()}`
                      ) : (
                        `${comp.sales_target || comp.target_value || 0} Sales`
                      )}
                    </p>
                    {comp.my_progress && (
                      <div className="mt-3">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-500">Progress</span>
                          <span className="font-medium text-slate-900">{comp.my_progress.percentage}%</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${comp.my_progress.achieved ? 'bg-green-500' : 'bg-indigo-500'}`}
                            style={{ width: `${Math.min(100, comp.my_progress.percentage)}%` }}
                          />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {comp.competition_type === 'revenue' ? '₹' : ''}{comp.my_progress.current_value?.toLocaleString()} / 
                          {comp.competition_type === 'revenue' ? '₹' : ''}{comp.my_progress.target_value?.toLocaleString()}
                        </p>
                      </div>
                    )}
                  </>
                )}
                
                <div className="mt-3 flex justify-between items-center">
                  <span className="text-xs text-slate-500">
                    Ends: {new Date(comp.end_date).toLocaleDateString()}
                  </span>
                  <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                    🏆 {comp.winner_type === 'staged' && comp.stages?.length > 0 
                      ? `Up to ${Math.max(...comp.stages.map(s => s.reward_points || 0))} pts`
                      : `${comp.prize_points_1st || comp.prize_points_all_achievers || 0} pts`
                    }
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 text-center py-4">No active competitions at the moment</p>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs uppercase text-slate-500 font-bold mb-1">Total Leads</p>
          <p className="text-2xl font-bold text-slate-900">{stats?.total_leads || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs uppercase text-slate-500 font-bold mb-1">Hot Leads</p>
          <p className="text-2xl font-bold text-red-600">{stats?.hot_leads || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs uppercase text-slate-500 font-bold mb-1">Customers</p>
          <p className="text-2xl font-bold text-emerald-600">{stats?.customers || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs uppercase text-slate-500 font-bold mb-1">Monthly Sales</p>
          <p className="text-2xl font-bold text-blue-600">{stats?.monthly_customers || 0}</p>
        </div>
      </div>

      {/* Point History - Show if has points */}
      {pointsData?.point_entries?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Gift className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-bold text-slate-900">Points History (This Month)</h2>
          </div>
          <div className="space-y-2">
            {pointsData.point_entries.map((entry, idx) => (
              <div key={idx} className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-200">
                <div>
                  <p className="font-medium text-green-800">{entry.competition_name}</p>
                  <p className="text-xs text-green-600">
                    {entry.position ? `Position: #${entry.position}` : 'Target Achieved'}
                  </p>
                </div>
                <span className="font-bold text-green-700">+{entry.points} pts</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesPerformance;
